import { isDefined } from "@clipboard-health/util-ts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { getContractorBadgeVerificationQueryKey } from "../api/useGetContractorBadgeVerification";
import { GRADE_FETCH_TIMEOUT_MS } from "../constants";
import { type BadgeVerificationPhoto, BadgeVerificationStep } from "../types";
import { useBadgeVerificationCamera } from "./useBadgeVerificationCamera";
import { type BadgeVerificationSubmitResult } from "./useBadgeVerificationSubmit";
import { useGradingFlow } from "./useGradingFlow";

/**
 * Delay (ms) before transitioning from the intro screen (button spinner)
 * to the full-screen grading screen. If the upload completes before this
 * delay, we skip straight to the GRADING step.
 */
const GRADING_SCREEN_DELAY_MS = 2_500;

interface UseBadgeVerificationPhotoFlowParams {
  shiftId: string;
  step: BadgeVerificationStep;
  photo: BadgeVerificationPhoto | undefined;
  setStep: (step: BadgeVerificationStep) => void;
  setPhoto: (photo: BadgeVerificationPhoto | undefined) => void;
  handleSubmit: (photo: BadgeVerificationPhoto) => Promise<BadgeVerificationSubmitResult>;
  onSubmitPhoto: () => void;
}

interface UseBadgeVerificationPhotoFlowResult {
  takePhoto: () => Promise<void>;
  handleTimedOutSubmit: () => void;
  gradingFlow: ReturnType<typeof useGradingFlow>;
  isUploadComplete: boolean;
}

/**
 * Orchestrates the photo → upload → grading → result flow.
 *
 * Manages the grading query, the 1-second upload delay gate, the 10-second
 * hard timeout on the GRADING step, and the retake-from-timeout path.
 */
export function useBadgeVerificationPhotoFlow(
  params: UseBadgeVerificationPhotoFlowParams
): UseBadgeVerificationPhotoFlowResult {
  const { shiftId, step, photo, setStep, setPhoto, handleSubmit, onSubmitPhoto } = params;

  const queryClient = useQueryClient();
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  // Keep a ref in sync with `step` so the async onPhotoTaken callback can
  // read the current step without stale closures.
  const stepRef = useRef(step);
  stepRef.current = step;

  const gradingFlow = useGradingFlow({ shiftId, enabled: isUploadComplete });

  // Transition out of GRADING when the grade query settles.
  // - Grade arrived → CONFIRM (show success or failure result)
  // - No grade returned (AI grading off) → GRADING_TIMED_OUT (same UX as
  //   timeout: retake is local-only, submit re-sends the image)
  const hasGradeResult = isDefined(gradingFlow.gradeResult);
  const { isQuerySettled } = gradingFlow;
  useEffect(() => {
    if (step !== BadgeVerificationStep.GRADING) {
      return;
    }

    if (hasGradeResult) {
      setStep(BadgeVerificationStep.CONFIRM);
    } else if (isQuerySettled) {
      setStep(BadgeVerificationStep.GRADING_TIMED_OUT);
    }
  }, [step, hasGradeResult, isQuerySettled, setStep]);

  // Hard timeout: if stuck on the GRADING screen, transition to
  // GRADING_TIMED_OUT so the user can retry or proceed.
  useEffect(() => {
    if (step !== BadgeVerificationStep.GRADING) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setStep(BadgeVerificationStep.GRADING_TIMED_OUT);
    }, GRADE_FETCH_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [step, setStep]);

  // Guard photo-dependent steps — if we somehow reach them without a photo
  // (e.g. deep-link with stale state), fall back to INTRO.
  useEffect(() => {
    const isPhotoStep =
      step === BadgeVerificationStep.GRADING ||
      step === BadgeVerificationStep.GRADING_TIMED_OUT ||
      step === BadgeVerificationStep.CONFIRM;
    if (isPhotoStep && !isDefined(photo)) {
      setStep(BadgeVerificationStep.INTRO);
    }
  }, [step, photo, setStep]);

  // Tracks whether the grading screen delay has elapsed so the submit
  // handler knows whether to transition to the GRADING step or stay on INTRO.
  const gradingDelayElapsedRef = useRef(false);

  const takePhoto = useBadgeVerificationCamera({
    onPhotoTaken: async (newPhoto) => {
      // Retake from timeout screen — just update the photo preview locally.
      // No network call; the user decides when to submit via the submit button.
      if (stepRef.current === BadgeVerificationStep.GRADING_TIMED_OUT) {
        setPhoto(newPhoto);
        return;
      }

      // When retaking from CONFIRM (grade result screen), skip the delay and
      // show the grading screen immediately. Without this, clearing the cached
      // grade below causes the CONFIRM step to briefly render the "Confirm
      // badge submission" screen instead of "Hang Tight".
      const isRetake = stepRef.current === BadgeVerificationStep.CONFIRM;

      setIsUploadComplete(false);
      gradingDelayElapsedRef.current = isRetake;

      // Clear cached grade data so a retake doesn't flash the old grade.
      queryClient.removeQueries({
        queryKey: getContractorBadgeVerificationQueryKey(shiftId, { includeFinalGrade: true }),
      });

      if (isRetake) {
        setPhoto(newPhoto);
        setStep(BadgeVerificationStep.GRADING);
      }

      // Start the delay timer — only relevant for first-time submissions
      // from INTRO where we show a button spinner before transitioning.
      const delayTimer = isRetake
        ? undefined
        : setTimeout(() => {
            gradingDelayElapsedRef.current = true;
            setPhoto(newPhoto);
            setStep(BadgeVerificationStep.GRADING);
          }, GRADING_SCREEN_DELAY_MS);

      const result = await handleSubmit(newPhoto);

      if (result.status === "success") {
        setIsUploadComplete(true);

        if (!gradingDelayElapsedRef.current) {
          // Upload finished before the delay — move to GRADING so the grade
          // query effect can transition to CONFIRM once the grade arrives.
          clearTimeout(delayTimer);
          setPhoto(newPhoto);
          setStep(BadgeVerificationStep.GRADING);
        }
      } else if (result.status === "alreadyCompleted") {
        clearTimeout(delayTimer);
        onSubmitPhoto();
      } else {
        // On error, show the old confirm screen so the user can retake or proceed.
        clearTimeout(delayTimer);
        setPhoto(newPhoto);
        setStep(BadgeVerificationStep.CONFIRM);
      }
    },
  });

  const handleTimedOutSubmit = useCallback(() => {
    onSubmitPhoto();
  }, [onSubmitPhoto]);

  return { takePhoto, handleTimedOutSubmit, gradingFlow, isUploadComplete };
}
